/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Universal Multi-Tabs by Rana
(function () {
  function initSKBUniversalMultiTabs(scope = document) {
    const containers = scope.querySelectorAll(".skb_universal_tabs_wrap");

    containers.forEach((container) => {
      const tabs = container.querySelectorAll("[data-skb-tab-button]");
      const contents = container.querySelectorAll("[data-skb-tab-content]");
      const indicator = container.querySelector(".skb_tab_nav__position"); // Optional

      const tabsWrapper = container.querySelector(".skb_tabs");

      // Move indicator under active tab
      function moveIndicator(tab) {
        if (!indicator || !tab || !tabsWrapper) return;

        const tabOffsetLeft = tab.offsetLeft;
        const tabWidth = tab.offsetWidth;

        indicator.style.width = `${tabWidth}px`;
        indicator.style.transform = `translateX(${tabOffsetLeft}px)`;
      }

      // Set initial state
      const firstActiveTab = container.querySelector(".skb_tab.skb_active") || tabs[0];
      if (firstActiveTab) {
        firstActiveTab.classList.add("skb_active");
        moveIndicator(firstActiveTab);
        const firstTarget = firstActiveTab.getAttribute("data-skb-tab-button");
        const firstContent = container.querySelector(`[data-skb-tab-content="${firstTarget}"]`);
        if (firstContent) {
          firstContent.classList.add("skb_show");
          firstContent.style.position = "relative";
        }
      }

      // Add click events
      tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const target = tab.getAttribute("data-skb-tab-button");

          tabs.forEach((t) => t.classList.remove("skb_active"));
          tab.classList.add("skb_active");

          moveIndicator(tab);
          scrollActiveTabIntoView(tab);

          contents.forEach((content) => {
            content.classList.remove("skb_show");
            content.style.position = "absolute";
          });

          const next = container.querySelector(`[data-skb-tab-content="${target}"]`);
          if (next) {
            next.classList.add("skb_show");
            next.style.position = "relative";
          }
        });
      });

      // Recalculate on window resize or scroll
      if (indicator) {
        window.addEventListener("resize", () => {
          const activeTab = container.querySelector(".skb_tab.skb_active");
          if (activeTab) moveIndicator(activeTab);
        });

        // Sync position while scrolling horizontally
        if (tabsWrapper) {
          tabsWrapper.addEventListener("scroll", () => {
            const activeTab = container.querySelector(".skb_tab.skb_active");
            if (activeTab) moveIndicator(activeTab);
          });
        }
      }

      // Scroll tab into view if needed
      function scrollActiveTabIntoView(tab) {
        if (!tabsWrapper || !tab) return;

        const containerLeft = tabsWrapper.scrollLeft;
        const offsetLeft = tab.offsetLeft;
        const offsetRight = offsetLeft + tab.offsetWidth;

        const visibleLeft = containerLeft;
        const visibleRight = containerLeft + tabsWrapper.clientWidth;

        if (offsetLeft < visibleLeft) {
          tabsWrapper.scrollTo({
            left: offsetLeft - 40,
            behavior: "smooth",
          });
        } else if (offsetRight > visibleRight) {
          tabsWrapper.scrollTo({
            left: offsetRight - tabsWrapper.clientWidth + 40,
            behavior: "smooth",
          });
        }
      }
    });
  }

  // DOM ready
  document.addEventListener("DOMContentLoaded", () => {
    initSKBUniversalMultiTabs();
  });

  // Shopify section reload support
  if (typeof Shopify !== "undefined" && Shopify.designMode) {
    document.addEventListener("shopify:section:load", (e) => {
      initSKBUniversalMultiTabs(e.target);
    });
  }
})();
// End of Universal Multi-Tabs

document.addEventListener('click', function (e) {
  if (e.target.closest('.discount_toggle')) {
    const parent = document.querySelector('.discount_toggle-main');
    parent.classList.toggle('expand_discount');
  }
});

/* Start: PDP | Collapse empty slots */
(() => {
  const CLS = 'product-form__slot--empty';
  const SKIP = 'script, style, link, meta, noscript, template';
  const done = new WeakSet();

  const empty = (el) => {
    if (!(el instanceof HTMLElement) || el.matches(SKIP)) return true;
    const had = el.classList.contains(CLS);
    const s = el.style;
    const prev = [s.display, s.position, s.visibility, s.height, s.pointerEvents];
    el.classList.remove(CLS);
    Object.assign(s, { display: 'block', position: 'absolute', visibility: 'hidden', height: 'auto', pointerEvents: 'none' });
    const h = el.offsetHeight;
    [s.display, s.position, s.visibility, s.height, s.pointerEvents] = prev;
    if (had) el.classList.add(CLS);
    return h < 1;
  };

  const update = (form) => [...form.children].forEach((c) => c.classList.toggle(CLS, empty(c)));

  const init = (form) => {
    if (done.has(form)) return update(form);
    done.add(form);
    update(form);
    let frame;
    new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = null; update(form); });
    }).observe(form, { childList: true, subtree: true, characterData: true });
    [300, 1000, 2500].forEach((ms) => setTimeout(() => update(form), ms));
  };

  const scan = (root) => (root || document).querySelectorAll('.product-form').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => scan()) : scan();
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
/* End: PDP | Collapse empty slots */
